import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { messages } from '@fruitways/shared';
import { Button, Icon, Picker, PickerField, type PickerOption } from '@/components';
import { useAdminFilters } from '@/app/AdminFilters';
import { growerOf, useGrowers, useIsAdmin } from '@/hooks/useAdmin';
import { AppFrame } from '@/app/AppFrame';
import { ProfileMenu } from '@/app/ProfileMenu';
import { useCapture } from '@/capture/CaptureContext';
import {
  openWeek,
  seasonForToday,
  useEstimates,
  useMasterData,
  varietiesForWeek,
} from '@/hooks/useMasterData';
import './capture.css';

/**
 * Step 1 of 3: choose the farm, then one or more varieties. An admin captures on behalf of a
 * grower, so first chooses the grower; the farm list then shows that grower's farms.
 */
export function SetupScreen() {
  const nav = useNavigate();
  const master = useMasterData();
  const estimates = useEstimates();
  const season = seasonForToday(master);
  // The submission cycle's open week, not the calendar one: growers estimate a week ahead.
  const week = openWeek();
  const seasonId = season?.id;
  const { draft, start, startEdit, update, toggleVariety, addVarieties } = useCapture();
  const [error, setError] = useState(false);
  const isAdmin = useIsAdmin();
  const growers = useGrowers();
  const filters = useAdminFilters();
  const [growerId, setGrowerId] = useState<string | null>(null);
  const [pickGrower, setPickGrower] = useState(false);

  // On arrival, a missing draft or a leftover edit draft becomes a fresh capture for this week.
  // A draft held over from an earlier submission window is stale too: its weekStart is last
  // week's, and submitting it would file the estimate against the wrong week.
  // Runs once: next() may switch the draft into edit mode and must not be undone here.
  const started = useRef(false);
  useEffect(() => {
    if (!seasonId || started.current) return;
    started.current = true;
    if (!draft || draft.editingId || draft.weekStart !== week.weekStart) {
      start({ weekNumber: week.weekNumber, weekStart: week.weekStart, seasonId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonId]);

  const order = master?.varieties.map((v) => v.code) ?? [];
  const farm = master?.farms.find((f) => f.id === draft?.farmId);
  const nextDisabled = !draft?.farmId || (draft?.picked.length ?? 0) === 0;
  const farmsOf = (id: string | null) =>
    id
      ? (master?.farms ?? []).filter((f) =>
          growers?.find((g) => g.id === id)?.farmIds.includes(f.id),
        )
      : [];
  /*
   * A farm already in for the open week drops off the list, so no second estimate can be started
   * for it; editing is reached from the estimate itself. The farm currently selected stays listed
   * either way, so it never disappears mid-flow. Admins see the same rule, across every grower.
   */
  const submittedFarmIds = new Set(
    (estimates ?? []).filter((e) => e.weekStart === week.weekStart).map((e) => e.farmId),
  );
  const outstanding = <T extends { id: string }>(farms: ReadonlyArray<T>): T[] =>
    farms.filter((f) => !submittedFarmIds.has(f.id) || f.id === draft?.farmId);
  /** A grower's farms that still need an estimate this week. */
  const openFarmsOf = (id: string | null) => outstanding(farmsOf(id));
  const farmChoices = isAdmin ? openFarmsOf(growerId) : outstanding(master?.farms ?? []);
  // What is in harvest for the week being captured, not every variety Fruitways packs.
  const varietyChoices = varietiesForWeek(master, seasonId, week.weekNumber);

  // Admin: start from the grower of the draft's farm, else the grower or farm chosen on Home.
  // A grower with one farm, or the farm chosen on Home, is selected straight away. Runs once,
  // and only once the estimates are in, so a farm already in for the week is never preselected.
  const adminStarted = useRef(false);
  useEffect(() => {
    if (!isAdmin || adminStarted.current || !growers || !master || !draft || !estimates) return;
    adminStarted.current = true;
    const g =
      growerOf(growers, draft.farmId)?.id ??
      filters.growerId ??
      growerOf(growers, filters.farmId)?.id ??
      null;
    setGrowerId(g);
    if (!draft.farmId && g) {
      // Only preselect a farm that still needs an estimate, so the pick is never a dead end.
      const own = openFarmsOf(g);
      const pre =
        own.find((f) => f.id === filters.farmId) ?? (own.length === 1 ? own[0] : undefined);
      if (pre) update({ farmId: pre.id, picked: [], values: {}, activeVariety: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, growers, master, draft, estimates]);

  function chooseGrower(id: string) {
    setGrowerId(id);
    setPickGrower(false);
    setError(false);
    const own = openFarmsOf(id);
    if (draft?.farmId && own.some((f) => f.id === draft.farmId)) return;
    update({
      farmId: own.length === 1 ? (own[0]?.id ?? null) : null,
      picked: [],
      values: {},
      activeVariety: null,
    });
  }

  /* Only growers with a farm still to capture for; the detail names those farms, not all of them. */
  const growerOptions: PickerOption<string>[] = (growers ?? [])
    .map((g) => ({ g, own: openFarmsOf(g.id) }))
    .filter(({ own }) => own.length > 0)
    .map(({ g, own }) => ({
      value: g.id,
      label: g.displayName,
      detail: messages.admin.filter.growerDetail(own.length, own.map((f) => f.name).join(', ')),
    }));
  const grower = growers?.find((g) => g.id === growerId);

  function next() {
    if (!draft || nextDisabled) {
      setError(true);
      return;
    }
    // An estimate already exists for this farm and week: continue as an edit of it, keeping
    // the varieties just chosen so nothing typed here is lost.
    const existing = estimates?.find(
      (e) => e.farmId === draft.farmId && e.weekStart === draft.weekStart,
    );
    if (existing) {
      const chosen = draft.picked;
      startEdit(existing);
      addVarieties(chosen, order);
    }
    if (draft.picked[0] && !draft.activeVariety) update({ activeVariety: draft.picked[0] });
    nav('/capture/entry');
  }

  return (
    <AppFrame
      title={messages.capture.title}
      subtitle={season ? messages.header.seasonWeek(season.label, week.weekNumber) : undefined}
      onBack={() => nav('/')}
      action={<ProfileMenu />}
    >
      <div className="fw-content" style={{ paddingBottom: 'var(--space-4)' }}>
        <span className="fw-secondary">{messages.capture.step1}</span>

        {isAdmin && (
          <>
            <span className="fw-inline-note">
              <Icon name="person-outline" size={16} style={{ marginTop: '2px' }} />
              {messages.admin.capture.forGrower}
            </span>
            {growerOptions.length === 0 && !grower ? (
              <span className="fw-secondary">{messages.capture.allFarmsIn}</span>
            ) : (
              <PickerField
                label={messages.admin.capture.selectGrower}
                required
                value={grower?.displayName}
                placeholder={messages.admin.capture.chooseGrower}
                onOpen={() => setPickGrower(true)}
              />
            )}
          </>
        )}

        {(!isAdmin || grower) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <span className="fw-label">{messages.capture.selectFarm}</span>
            {farmChoices.length === 0 && (
              <span className="fw-secondary">{messages.capture.allFarmsIn}</span>
            )}
            <div className="fw-rows" hidden={farmChoices.length === 0}>
              {farmChoices.map((f) => {
                const on = draft?.farmId === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    className="fw-farm-row"
                    aria-pressed={on}
                    onClick={() => {
                      update({ farmId: f.id, picked: [], values: {}, activeVariety: null });
                      setError(false);
                    }}
                  >
                    <Icon
                      name={on ? 'radio-button-on' : 'radio-button-off'}
                      size={24}
                      color={on ? 'var(--color-primary)' : 'var(--color-border-strong)'}
                    />
                    <span className="fw-farm-row__name">{f.name}</span>
                    {isAdmin && (
                      <span className="fw-secondary fw-tnum">
                        {messages.admin.filter.pucDetail(f.puc)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {farm && draft && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <span className="fw-label">{messages.capture.selectVarieties}</span>
            <span className="fw-secondary">{messages.capture.chooseOneOrMore(farm.name)}</span>
            <div className="fw-chip-grid">
              {varietyChoices.map((code) => {
                const on = draft.picked.includes(code);
                return (
                  <button
                    key={code}
                    type="button"
                    className={`fw-chip${on ? ' fw-chip--on' : ''}`}
                    aria-pressed={on}
                    onClick={() => {
                      toggleVariety(code, order);
                      setError(false);
                    }}
                  >
                    {code}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {error && (
          <span className="fw-inline-error" role="alert">
            <Icon name="alert-circle" size={16} style={{ marginTop: '2px' }} />
            {isAdmin ? messages.admin.capture.growerRequired : messages.capture.step1Error}
          </span>
        )}
      </div>
      <Picker
        open={pickGrower}
        title={messages.admin.filter.chooseGrower}
        options={growerOptions}
        value={growerId ?? ''}
        closeLabel={messages.common.close}
        searchLabel={messages.admin.filter.search}
        searchPlaceholder={messages.admin.filter.searchGrower}
        noMatch={messages.admin.filter.noMatch}
        onClose={() => setPickGrower(false)}
        onPick={chooseGrower}
      />
      <div className="fw-actions">
        <Button
          variant="secondary"
          onClick={() => {
            nav('/');
          }}
        >
          {messages.common.cancel}
        </Button>
        <Button onClick={next} disabled={nextDisabled} iconEnd="arrow-forward">
          {messages.common.next}
        </Button>
      </div>
    </AppFrame>
  );
}
